<template>
  <div class="pb-2 container-fluid d-flex h-100 flex-column">
    <div class="row g-0">
      <div class="col">
        <h5 class="current">{{ t("billing_summary.current_plan") }}</h5>
      </div>
    </div>
    <div class="row align-items-center">
      <div class="col">
        <div class="row g-0">
          <p class="card-text subscription-plan">{{ planLabels[currentPlanName] || "" }}</p>
        </div>
        <div class="row g-0 pb-0">
          <p class="card-text subscription-price">
            {{ currency(billingDetails.planPrice || 0) }} / {{ t(`billing_summary.${billingDetails.paymentInterval}`) }}
          </p>
        </div>
        <div class="row g-0">
          <p class="card-text subscription-renewal">{{ t("billing_summary.your_plan_renews_on") }} {{ formatLongDate(billingDetails.renewalDate) }}</p>
        </div>
      </div>
    </div>

    <div class="row g-0 pt-4">
      <div class="col">
        <div class="row g-0 current align-items-center">
          <div class="col">
            <h5 class="mb-0">{{ t("billing_summary.plans_available") }}</h5>
          </div>
          <div class="col-auto d-flex align-items-center">
            <span>{{ t("subscription.monthly") }}</span>
            <div class="form-check form-switch mx-2 mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="update-plan-interval-toggle"
                :checked="displayInterval === 'yearly'"
                @change="displayInterval = ($event.target as HTMLInputElement).checked ? 'yearly' : 'monthly'"
              />
            </div>
            <span>{{ t("subscription.yearly") }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-for="plan in orderedPlans" :key="plan.name" class="row pt-3 g-0 current">
      <div class="col">
        <div class="row g-0 align-items-center">
          <div class="col-4">
            <p class="card-text subscription-plan pe-4 mb-0">{{ planLabels[plan.name as string] }}</p>
          </div>
          <div class="col">
            <p class="card-text subscription-price mb-0">{{ displayPrice(plan) }}</p>
          </div>
          <div class="text-end col-auto">
            <button
              v-if="planAction(plan) === 'current'"
              type="button"
              class="btn btn-secondary btn-sm rounded-pill select-button"
              disabled
              style="opacity: 0.5"
            >
              {{ t("billing_summary.current") }}
            </button>
            <button
              v-else-if="planAction(plan) === 'pending'"
              type="button"
              class="btn btn-secondary btn-sm rounded-pill select-button"
              disabled
              style="opacity: 0.5"
            >
              {{ t("billing_summary.scheduled") }}
            </button>
            <button
              v-else-if="planAction(plan) === 'scheduled'"
              type="button"
              class="btn btn-outline-primary btn-sm rounded-pill select-button"
              @click="scheduleChange(plan)"
            >
              {{ t("billing_summary.switch_at_renewal") }}
            </button>
            <button v-else type="button" class="btn btn-primary btn-sm rounded-pill select-button" @click="changeNow(plan)">
              {{ plan.name === currentPlanName ? t("billing_summary.switch_to_yearly") : t("billing_summary.select") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="currentInterval === 'yearly'" class="row g-0 pt-2">
      <div class="col">
        <p class="card-text text-muted renewal-note mb-0">
          {{ t("billing_summary.renewal_change_note", { date: formatLongDate(billingDetails.renewalDate) }) }}
        </p>
      </div>
    </div>

    <div class="row g-0 text-end row-bottom py-4">
      <div class="col align-right px-0">
        <div class="btn-group" role="group">
          <button class="btn btn-primary btn-sm rounded-pill" type="button" @click="summary">{{ t("billing_summary.back_to_summary") }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Billing from "../../models/Billing";
  import Plan from "../../models/Plan";
  import { ref, computed, inject, onBeforeMount } from "vue";
  import { scheduleChange as scheduleChangeRequest } from "@/services/PaymentService";
  import { useStore } from "vuex";
  import { useToast } from "vue-toastification";
  import { useI18n } from "vue-i18n";

  const $store = useStore();
  const emitter: any = inject("emitter");
  const toast = useToast();
  const { t } = useI18n();

  const props = defineProps<{
    billingDetails: Billing;
  }>();

  const planLabels: Record<string, string> = {
    basic: "Basic",
    pro: "Professional",
    enterprise: "Enterprise",
  };

  const tierOrder = ["basic", "pro", "enterprise"];

  const orderedPlans = computed((): Plan[] => {
    const plans: Plan[] = $store.getters.plans || [];
    return tierOrder.map((name) => plans.find((p) => p.name === name)).filter((p): p is Plan => !!p);
  });

  // Billing data uses "Basic" / "Pro" / "Enterprise"; plans use "basic" / "pro" / "enterprise"
  const currentPlanName = computed((): string => props.billingDetails.subscriptionName?.toLowerCase() || "");

  // Normalize "year"/"yearly" and "month"/"monthly"
  const currentInterval = computed((): string => (props.billingDetails.paymentInterval?.startsWith("year") ? "yearly" : "monthly"));

  const displayInterval = ref(currentInterval.value);

  onBeforeMount(() => {
    $store.dispatch("getPlans");
  });

  // current   = same plan and interval
  // immediate = monthly -> anything, or yearly -> higher-tier yearly (prorated)
  // scheduled = yearly -> lower-tier yearly, or yearly -> any monthly (takes effect at renewal)
  function planAction(plan: Plan): "current" | "pending" | "immediate" | "scheduled" {
    const target = plan.name as string;
    const pending = props.billingDetails.pendingChange;
    if (pending && pending.plan === target && pending.interval === displayInterval.value) {
      return "pending";
    }
    if (target === currentPlanName.value && displayInterval.value === currentInterval.value) {
      return "current";
    }
    if (currentInterval.value === "monthly") {
      return "immediate";
    }
    if (displayInterval.value === "monthly") {
      return "scheduled";
    }
    return tierOrder.indexOf(target) > tierOrder.indexOf(currentPlanName.value) ? "immediate" : "scheduled";
  }

  function displayPrice(plan: Plan) {
    if (displayInterval.value === "yearly") {
      return `${currency(plan.annualPrice ?? 0)} / ${t("billing_add_ons.period_year")}`;
    }
    return `${currency(plan.price ?? 0)} / ${t("billing_add_ons.period_month")}`;
  }

  function changeNow(plan: Plan) {
    emitter.emit("billing-modal-close");
    emitter.emit("payment-modal", { planType: plan, upgrade: true, billingInterval: displayInterval.value });
  }

  // function scheduleChange(plan: Plan) {
  //   const change = {
  //     plan: plan.name,
  //     interval: displayInterval.value,
  //     effectiveDate: props.billingDetails.renewalDate,
  //   };

  //   // TODO: replace with the scheduled-change API call once the backend/Stripe work is done
  //   console.log(change, "scheduled plan change");

  //   toast.success(t("billing_summary.scheduled_change_success", { date: formatLongDate(props.billingDetails.renewalDate) }));
  // }
  async function scheduleChange(plan: Plan) {
    const response = await scheduleChangeRequest({
      subscriptionType: plan.name as string,
      interval: displayInterval.value,
    });

    if (response.status === "success") {
      toast.success(t("billing_summary.scheduled_change_success", { date: formatLongDate(response.effectiveDate) }));
      emitter.emit("billing-summary", "refresh");
    } else {
      toast.error(t("billing_summary.scheduled_change_failed"));
    }
  }

  function currency(value: number) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
  }

  function formatLongDate(value?: string | null) {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  function summary() {
    emitter.emit("billing-summary");
  }
</script>

<style scoped>
  .current {
    border-bottom: 1px solid lightgray;
    padding-bottom: 0.5rem;
  }

  .subscription-plan {
    font-family: "proximanova-bold", sans-serif;
    font-size: 20px;
  }

  .subscription-price {
    font-family: "proximanova-regular", sans-serif;
    font-size: 20px;
  }

  .subscription-renewal {
    margin-top: -0.5rem;
    font-family: "proximanova-regular", sans-serif;
    font-size: 16px;
    white-space: nowrap;
  }

  .renewal-note {
    font-size: 12px;
  }

  h5 {
    font-family: "proximanova-medium", sans-serif !important;
  }

  @media screen and (max-width: 576px) {
    .subscription-price {
      font-size: 16px;
    }

    .subscription-plan {
      font-size: 16px;
    }
  }
</style>
